import { Box, styled } from '@mui/system';
import { List, ListItem, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import textData from '../data/data.json';
import { selectedLanguageAtom } from '../recoil/atoms/languageAtom';
import { useRecoilValue } from 'recoil';

const Wrapper = styled('div')(({ has_max_height }) => ({
    flex: '0 0 100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '50px',
    alignItems: 'center',
    minHeight: '300px',
    width: '100%',
    maxWidth: '100%',
    maxHeight: has_max_height ? 'calc(100svh - 200px)' : 'unset',

    overflowY: 'auto',
}));

const Title = styled(Typography)({
    fontSize: '30px',
});

export const Caption = styled(Typography)(({ theme }) => ({
    fontSize: 14,
    opacity: 0.8,
    lineHeight: 1.5,
    padding: '0 10px',
}));

export const AccentCaption = styled(Caption)(({ theme }) => ({
    backgroundColor: '#eee',
    borderRadius: 10,
    padding: '12px 20px',
    opacity: 1,
}));

export const StepTitleBlock = styled(Typography)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 12,
}));

export const StepTitle = styled(Typography)(({ theme }) => ({
    lineHeight: 1.2,
}));

export const StepBlock = styled(Box)(({ topSpace }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 32,

    marginTop: topSpace ? 32 : 0,
}));

export const StepInnerBlock = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
}));

export const Coin = styled(Box)(({ theme }) => ({
    minWidth: 28,
    width: 28,
    height: 28,

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    color: 'white',
    fontSize: 14,
    lineHeight: 1,

    backgroundColor: '#3B7AF6',
    borderRadius: 16,
}));

const CustomListItem = ({ children }) => {
    return (
        <ListItem
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
            }}
        >
            <Box
                sx={{
                    minWidth: 12,
                    width: 12,
                    height: 12,
                    borderRadius: 10,
                    border: '2px solid #3B7AF6',
                    backgroundColor: 'rgb(238, 242, 252)',
                }}
            />
            {children}
        </ListItem>
    );
};

const QuizInstructions = ({
    isLeader,
    isFirstInstruction,
    isLeaderGuide,
    setInstructionSlidesNumber = null,
    hasMaxHeight = true,
}) => {
    useEffect(() => {
        if (!setInstructionSlidesNumber) {
            return;
        }

        if (isLeader && isFirstInstruction) {
            setInstructionSlidesNumber(5);
        } else {
            setInstructionSlidesNumber(1);
        }
    }, [isLeader, isFirstInstruction, setInstructionSlidesNumber]);

    if (isLeaderGuide) {
        return <AdminInstructionsOnStepsPage hasMaxHeight={hasMaxHeight} />;
    }

    if (!isLeader) {
        return isFirstInstruction ? (
            <UserInstructions1 hasMaxHeight={hasMaxHeight} />
        ) : (
            <UserInstructions2 hasMaxHeight={hasMaxHeight} />
        );
    }

    return isFirstInstruction ? (
        <AdminInstructions1 hasMaxHeight={hasMaxHeight} />
    ) : (
        <AdminInstructions2 hasMaxHeight={hasMaxHeight} />
    );
};

const UserInstructions1 = ({ hasMaxHeight }) => {
    const selectedLanguage = useRecoilValue(selectedLanguageAtom);
    const { userInstructions1 } = textData[selectedLanguage];
    return (
        <Wrapper has_max_height={hasMaxHeight}>
            <Title variant={'h1'}>{userInstructions1.title}</Title>

            <Caption>{userInstructions1.captions[0]}</Caption>
            <AccentCaption dangerouslySetInnerHTML={{ __html: userInstructions1.captions[1] }} />

            <StepInnerBlock>
                <Caption sx={{ textAlign: 'left' }}>{userInstructions1.captions[2]}</Caption>
                <List>
                    <CustomListItem>
                        <Caption sx={{ textAlign: 'left' }}>{userInstructions1.captions[3]}</Caption>
                    </CustomListItem>
                    <CustomListItem>
                        <Caption sx={{ textAlign: 'left' }}>{userInstructions1.captions[4]}</Caption>
                    </CustomListItem>
                    <CustomListItem>
                        <Caption sx={{ textAlign: 'left' }}>{userInstructions1.captions[5]}</Caption>
                    </CustomListItem>
                </List>
            </StepInnerBlock>

            <StepInnerBlock>
                <Caption dangerouslySetInnerHTML={{ __html: userInstructions1.captions[6] }} />
                <Caption>{userInstructions1.captions[7]}</Caption>
                <AccentCaption>{userInstructions1.captions[8]}</AccentCaption>
                <Caption>{userInstructions1.captions[9]}</Caption>
            </StepInnerBlock>
        </Wrapper>
    );
};

const UserInstructions2 = ({ hasMaxHeight }) => {
    const selectedLanguage = useRecoilValue(selectedLanguageAtom);
    const { userInstructions2 } = textData[selectedLanguage];

    return (
        <Wrapper has_max_height={hasMaxHeight}>
            <Title variant={'h1'}>{userInstructions2.title}</Title>
            <Caption>{userInstructions2.caption}</Caption>

            <List>
                <Caption sx={{ textAlign: 'left' }}>
                    <strong>{userInstructions2.list.heading}</strong>
                </Caption>
                <CustomListItem>
                    <Caption
                        sx={{ textAlign: 'left' }}
                        dangerouslySetInnerHTML={{ __html: userInstructions2.list.items[0] }}
                    />
                </CustomListItem>
                <CustomListItem>
                    <Caption
                        sx={{ textAlign: 'left' }}
                        dangerouslySetInnerHTML={{ __html: userInstructions2.list.items[1] }}
                    />
                </CustomListItem>
            </List>

            <Caption sx={{ width: '100%' }}>
                <strong>{userInstructions2.meetingChoices.heading}</strong>
            </Caption>
            <Caption dangerouslySetInnerHTML={{ __html: userInstructions2.meetingChoices.positive }} />
            <Caption dangerouslySetInnerHTML={{ __html: userInstructions2.meetingChoices.negative }} />

            <Caption dangerouslySetInnerHTML={{ __html: userInstructions2.finalNotes[0] }} />
            <Caption dangerouslySetInnerHTML={{ __html: userInstructions2.finalNotes[1] }} />
        </Wrapper>
    );
};

const AdminInstructions1 = ({ hasMaxHeight }) => {
    const selectedLanguage = useRecoilValue(selectedLanguageAtom);
    const { adminInstructions1 } = textData[selectedLanguage];
    return (
        <>
            <Wrapper has_max_height={hasMaxHeight}>
                <Title variant={'h1'}>{adminInstructions1.greeting.title}</Title>
                <Caption>{adminInstructions1.greeting.caption}</Caption>

                <StepBlock>
                    <StepTitleBlock>
                        <Coin>1</Coin>
                        <StepTitle>{adminInstructions1.steps.step1.title}</StepTitle>
                    </StepTitleBlock>

                    <StepInnerBlock>
                        <List>
                            <Caption
                                sx={{ textAlign: 'left' }}
                                dangerouslySetInnerHTML={{ __html: adminInstructions1.steps.step1.items[0] }}
                            />
                            <CustomListItem>
                                <Caption sx={{ textAlign: 'left' }}>{adminInstructions1.steps.step1.items[1]}</Caption>
                            </CustomListItem>
                            <CustomListItem>
                                <Caption sx={{ textAlign: 'left' }}>{adminInstructions1.steps.step1.items[2]}</Caption>
                            </CustomListItem>
                            <CustomListItem>
                                <Caption sx={{ textAlign: 'left' }}>{adminInstructions1.steps.step1.items[3]}</Caption>
                            </CustomListItem>
                        </List>
                        <Caption
                            sx={{ textAlign: 'left' }}
                            dangerouslySetInnerHTML={{ __html: adminInstructions1.steps.step1.items[4] }}
                        />
                    </StepInnerBlock>
                </StepBlock>
            </Wrapper>

            <Wrapper has_max_height={hasMaxHeight}>
                <StepBlock topSpace={true}>
                    <StepTitleBlock>
                        <Coin>2</Coin>
                        <StepTitle>{adminInstructions1.steps.step2.title}</StepTitle>
                    </StepTitleBlock>

                    <StepInnerBlock>
                        <Caption
                            sx={{ textAlign: 'left' }}
                            dangerouslySetInnerHTML={{ __html: adminInstructions1.steps.step2.items[0] }}
                        />
                        <Caption
                            sx={{ textAlign: 'left' }}
                            dangerouslySetInnerHTML={{ __html: adminInstructions1.steps.step2.items[1] }}
                        />
                    </StepInnerBlock>
                </StepBlock>
            </Wrapper>

            <Wrapper has_max_height={hasMaxHeight}>
                <StepBlock topSpace={true}>
                    <StepTitleBlock>
                        <Coin>3</Coin>
                        <StepTitle>{adminInstructions1.steps.step3.title}</StepTitle>
                    </StepTitleBlock>

                    <StepInnerBlock>
                        <Caption
                            sx={{ textAlign: 'left' }}
                            dangerouslySetInnerHTML={{ __html: adminInstructions1.steps.step3.items[0] }}
                        />
                        <Caption
                            sx={{ textAlign: 'left' }}
                            dangerouslySetInnerHTML={{ __html: adminInstructions1.steps.step3.items[1] }}
                        />
                        <List>
                            <Caption sx={{ textAlign: 'left' }}>{adminInstructions1.steps.step3.items[2]}</Caption>
                            <CustomListItem>
                                <Caption sx={{ textAlign: 'left' }}>{adminInstructions1.steps.step3.items[3]}</Caption>
                            </CustomListItem>
                            <CustomListItem>
                                <Caption sx={{ textAlign: 'left' }}>{adminInstructions1.steps.step3.items[4]}</Caption>
                            </CustomListItem>
                        </List>
                    </StepInnerBlock>
                </StepBlock>
            </Wrapper>

            <Wrapper has_max_height={hasMaxHeight}>
                <StepBlock topSpace={true}>
                    <StepTitleBlock>
                        <Coin>4</Coin>
                        <StepTitle>{adminInstructions1.steps.step4.title}</StepTitle>
                    </StepTitleBlock>

                    <StepInnerBlock>
                        <Caption
                            sx={{ textAlign: 'left' }}
                            dangerouslySetInnerHTML={{ __html: adminInstructions1.steps.step4.items[0] }}
                        />
                        <List>
                            <Caption
                                sx={{ textAlign: 'left' }}
                                dangerouslySetInnerHTML={{ __html: adminInstructions1.steps.step4.items[1] }}
                            />
                            <CustomListItem>
                                <Caption sx={{ textAlign: 'left' }}>{adminInstructions1.steps.step4.items[2]}</Caption>
                            </CustomListItem>
                            <CustomListItem>
                                <Caption sx={{ textAlign: 'left' }}>{adminInstructions1.steps.step4.items[3]}</Caption>
                            </CustomListItem>
                        </List>
                        <List>
                            <Caption
                                sx={{ textAlign: 'left' }}
                                dangerouslySetInnerHTML={{ __html: adminInstructions1.steps.step4.items[4] }}
                            />
                            <CustomListItem>
                                <Caption sx={{ textAlign: 'left' }}>{adminInstructions1.steps.step4.items[5]}</Caption>
                            </CustomListItem>
                            <CustomListItem>
                                <Caption sx={{ textAlign: 'left' }}>{adminInstructions1.steps.step4.items[6]}</Caption>
                            </CustomListItem>
                        </List>
                        <Caption sx={{ textAlign: 'left' }}>{adminInstructions1.steps.step4.items[7]}</Caption>
                    </StepInnerBlock>
                </StepBlock>
            </Wrapper>

            <Wrapper has_max_height={hasMaxHeight}>
                <StepBlock topSpace={true}>
                    <StepTitleBlock>
                        <Coin>5</Coin>
                        <StepTitle>{adminInstructions1.steps.step5.title}</StepTitle>
                    </StepTitleBlock>

                    <StepInnerBlock>
                        <List>
                            <CustomListItem>
                                <Caption
                                    sx={{ textAlign: 'left' }}
                                    dangerouslySetInnerHTML={{ __html: adminInstructions1.steps.step5.items[0] }}
                                />
                            </CustomListItem>
                            <CustomListItem>
                                <Caption
                                    sx={{ textAlign: 'left' }}
                                    dangerouslySetInnerHTML={{ __html: adminInstructions1.steps.step5.items[1] }}
                                />
                            </CustomListItem>
                        </List>
                        <Caption
                            sx={{ textAlign: 'left' }}
                            dangerouslySetInnerHTML={{ __html: adminInstructions1.steps.step5.items[2] }}
                        />
                    </StepInnerBlock>
                </StepBlock>
            </Wrapper>
        </>
    );
};

const AdminInstructions2 = ({ hasMaxHeight }) => {
    const selectedLanguage = useRecoilValue(selectedLanguageAtom);
    const { adminInstructions2 } = textData[selectedLanguage];
    return (
        <Wrapper has_max_height={hasMaxHeight}>
            <Title variant={'h1'} dangerouslySetInnerHTML={{ __html: adminInstructions2.title }} />
            <Caption dangerouslySetInnerHTML={{ __html: adminInstructions2.caption1 }} />
            <StepTitle sx={{ padding: '0 10px' }} dangerouslySetInnerHTML={{ __html: adminInstructions2.subtitle }} />
            <Caption dangerouslySetInnerHTML={{ __html: adminInstructions2.caption2 }} />
            <List>
                <Caption
                    sx={{ textAlign: 'left' }}
                    dangerouslySetInnerHTML={{ __html: adminInstructions2.whatToDo.title }}
                />
                {adminInstructions2.whatToDo.items.map((item, index) => (
                    <CustomListItem key={index}>
                        <Caption sx={{ textAlign: 'left' }} dangerouslySetInnerHTML={{ __html: item }} />
                    </CustomListItem>
                ))}
            </List>
            <Caption dangerouslySetInnerHTML={{ __html: adminInstructions2.footer }} />
        </Wrapper>
    );
};

const AdminInstructionsOnStepsPage = ({ hasMaxHeight }) => {
    const selectedLanguage = useRecoilValue(selectedLanguageAtom);
    const { adminInstructionsOnStepsPage } = textData[selectedLanguage];
    return (
        <Wrapper has_max_height={hasMaxHeight}>
            <Title variant={'h1'}>{adminInstructionsOnStepsPage.title}</Title>
            <Caption>{adminInstructionsOnStepsPage.introCaption1}</Caption>
            <Caption>{adminInstructionsOnStepsPage.introCaption2}</Caption>

            <StepBlock>
                <StepTitleBlock>
                    <Coin>1</Coin>
                    <StepTitle>{adminInstructionsOnStepsPage.step1.title}</StepTitle>
                </StepTitleBlock>

                <StepInnerBlock>
                    <List>
                        <CustomListItem>
                            <Caption
                                sx={{ textAlign: 'left' }}
                                dangerouslySetInnerHTML={{ __html: adminInstructionsOnStepsPage.step1.listItems[0] }}
                            />
                        </CustomListItem>
                        <CustomListItem>
                            <Caption
                                sx={{ textAlign: 'left' }}
                                dangerouslySetInnerHTML={{ __html: adminInstructionsOnStepsPage.step1.listItems[1] }}
                            />
                        </CustomListItem>
                        <CustomListItem>
                            <Caption
                                sx={{ textAlign: 'left' }}
                                dangerouslySetInnerHTML={{ __html: adminInstructionsOnStepsPage.step1.listItems[2] }}
                            />
                        </CustomListItem>
                    </List>
                </StepInnerBlock>
            </StepBlock>

            <StepBlock>
                <StepTitleBlock>
                    <Coin>2</Coin>
                    <StepTitle>{adminInstructionsOnStepsPage.step2.title}</StepTitle>
                </StepTitleBlock>

                <StepInnerBlock>
                    <Caption
                        sx={{ textAlign: 'left' }}
                        dangerouslySetInnerHTML={{ __html: adminInstructionsOnStepsPage.step2.caption1 }}
                    />
                    <Caption
                        sx={{ textAlign: 'left' }}
                        dangerouslySetInnerHTML={{ __html: adminInstructionsOnStepsPage.step2.caption2 }}
                    />
                    <List>
                        <CustomListItem>
                            <Caption sx={{ textAlign: 'left' }}>
                                {adminInstructionsOnStepsPage.step2.listItems[0]}
                            </Caption>
                        </CustomListItem>
                        <CustomListItem>
                            <Caption sx={{ textAlign: 'left' }}>
                                {adminInstructionsOnStepsPage.step2.listItems[1]}
                            </Caption>
                        </CustomListItem>
                        <CustomListItem>
                            <Caption sx={{ textAlign: 'left' }}>
                                {adminInstructionsOnStepsPage.step2.listItems[2]}
                            </Caption>
                        </CustomListItem>
                        <CustomListItem>
                            <Caption
                                sx={{ textAlign: 'left' }}
                                dangerouslySetInnerHTML={{ __html: adminInstructionsOnStepsPage.step2.listItems[3] }}
                            />
                        </CustomListItem>
                    </List>
                    <Caption
                        sx={{ textAlign: 'left' }}
                        dangerouslySetInnerHTML={{ __html: adminInstructionsOnStepsPage.step2.caption3 }}
                    />
                </StepInnerBlock>
            </StepBlock>

            <StepBlock>
                <StepTitleBlock>
                    <Coin>3</Coin>
                    <StepTitle>{adminInstructionsOnStepsPage.step3.title}</StepTitle>
                </StepTitleBlock>

                <StepInnerBlock>
                    <List>
                        <CustomListItem>
                            <Caption
                                sx={{ textAlign: 'left' }}
                                dangerouslySetInnerHTML={{ __html: adminInstructionsOnStepsPage.step3.listItems[0] }}
                            />
                        </CustomListItem>
                        <CustomListItem>
                            <Caption
                                sx={{ textAlign: 'left' }}
                                dangerouslySetInnerHTML={{ __html: adminInstructionsOnStepsPage.step3.listItems[1] }}
                            />
                        </CustomListItem>
                        <CustomListItem>
                            <Caption
                                sx={{ textAlign: 'left' }}
                                dangerouslySetInnerHTML={{ __html: adminInstructionsOnStepsPage.step3.listItems[2] }}
                            />
                        </CustomListItem>
                    </List>
                    <Caption
                        sx={{ textAlign: 'left' }}
                        dangerouslySetInnerHTML={{ __html: adminInstructionsOnStepsPage.step3.caption }}
                    />
                </StepInnerBlock>
            </StepBlock>
            <Caption
                sx={{ textAlign: 'left' }}
                dangerouslySetInnerHTML={{ __html: adminInstructionsOnStepsPage.footer }}
            />
        </Wrapper>
    );
};

export default QuizInstructions;
